<template>
	<ViewFeedItem
		class="view-feed-tvplayingnow"
		v-bind:class="{
			'state-interacted': hasInteracted,
			'state-videopreview': showVideoPreview,
		}"
		v-bind:style="{
			'--drag-offset': dragRatio,
		}"
	>
		<template v-slot:body>
			<!-- Live Schedule -->
			<ChalkyTvNowPlaying
				class="show-image show-tags stacked animate-in"
				v-bind:class="{
					'state-active': activeIndex === index,
				}"
				v-bind:gameType="game"
				v-bind:key="refreshKey + '-' + index"
				v-for="(game, index) in games"
			>
				<template>
					<section>
						<button
							class="button button-primary watch-now"
							v-on:click="Handle_OnClickWatchNow($event, index)"
						>
							<span>Watch Now</span>
						</button>
					</section>
				</template>

				<template v-slot:after>
					<!-- Disabling the autoplay, it's a big load -->
					<!-- <ChalkyTvVideoTheater
						v-bind:allowControl="false"
						v-bind:channel="game"
						v-bind:mute="true"
						v-bind:scheduleModel="tvLiveModel"
						v-if="index == 0"
						v-on:video:starting="Handle_OnVideoPreviewStarting"
					/> -->
				</template>
			</ChalkyTvNowPlaying>
		</template>

		<template v-slot:footer>
			<ViewUiStateDots v-if="dotCount > 1" v-bind:activeIndex="activeIndex" v-bind:amount="dotCount" />
		</template>
	</ViewFeedItem>
</template>

<script lang="ts">
	import ChalkySticks from '@chalkysticks/sdk';
	import ViewBase from '@/Core/View.vue';
	import gsap from 'gsap';
	import { Component, Prop } from 'vue-property-decorator';
	import { beforeDestroy, bind, event, mounted } from '@/Utility/Decorators';

	/**
	 * @author Matt Kenefick <matt@chalkysticks.com>
	 * @package View/Feed
	 * @project ChalkySticks App
	 */
	@Component
	export default class ViewFeedTVPlayingNow extends ViewBase {
		/**
		 * @type ChalkySticks.Core.Input.Pointer
		 */
		protected get pointer(): ChalkySticks.Core.Input.Pointer {
			const pointer = new ChalkySticks.Core.Input.Pointer('pointer', true, this.$el as HTMLElement);
			return pointer;
		}

		/**
		 * @return number
		 */
		protected get dotCount(): number {
			return this.tvLiveCollection?.length > 0 ? this.tvLiveCollection.length : this.games.length;
		}

		/**
		 * @type number
		 */
		protected activeIndex: number = 0;

		/**
		 * @type boolean
		 */
		protected automatic: boolean = true;

		/**
		 * @type ChalkySticks/Model/Authentication
		 */
		protected games: ChalkySticks.Enum.GameType[] = [
			ChalkySticks.Enum.GameType.EightBall,
			ChalkySticks.Enum.GameType.NineBall,
			ChalkySticks.Enum.GameType.TenBall,
			ChalkySticks.Enum.GameType.StraightPool,
			ChalkySticks.Enum.GameType.Snooker,
		];

		/**
		 * @type number
		 */
		protected dragRatio: number = 0;

		/**
		 * @type boolean
		 */
		protected hasInteracted: boolean = false;

		/**
		 * @type number
		 */
		protected intervalDuration: number = 1000 * 5;

		/**
		 * @type string
		 */
		protected refreshKey: string = '0';

		/**
		 * @type boolean
		 */
		protected showVideoPreview: boolean = false;

		/**
		 * Directly assigning these with "=" caused stack overflow
		 * merge issues when returning back to a page with this on it.
		 *
		 * @type ChalkySticks.TV.Collection.Live
		 */
		protected tvLiveCollection!: ChalkySticks.TV.Collection.Live;

		/**
		 * Directly assigning these with "=" caused stack overflow
		 * merge issues when returning back to a page with this on it.
		 *
		 * @type ChalkySticks.TV.Model.Schedule
		 */
		protected tvLiveModel!: ChalkySticks.TV.Model.Schedule;

		/**
		 * Directly assigning these with "=" caused stack overflow
		 * merge issues when returning back to a page with this on it.
		 *
		 * @type ChalkySticks.TV.Collection.Schedule
		 */
		protected tvScheduleCollection!: ChalkySticks.TV.Collection.Schedule;

		/**
		 * mk: Activate triggers when the app launches for the first time (and nothing is ready)
		 *
		 * It previously used @event('activate')
		 *
		 * @return Promise<void>
		 */
		@mounted
		public async setup(): Promise<void> {
			this.tvScheduleCollection = ChalkySticks.TV.Factory.Schedule.collection();
			this.tvLiveCollection = ChalkySticks.TV.Factory.Live.collection();

			this.refreshKey = Math.random().toString(36).substring(7);

			// Fetch live videos
			// await this.tvLiveCollection.fetch();
		}

		/**
		 * @return void
		 */
		@mounted
		public attachEvents(): void {
			this.pointer.on('tap', this.Handle_OnTap);
			this.pointer.on('drag', this.Handle_OnDrag);
			this.pointer.on('up', this.Handle_OnDragRelease);
		}

		/**
		 * @return void
		 */
		@beforeDestroy
		public detachEvents(): void {
			this.pointer.off('tap');
			this.pointer.off('drag');
			this.pointer.off('up');
		}

		/**
		 * @return void
		 */
		protected next() {
			this.activeIndex = this.activeIndex + 1;

			if (this.activeIndex >= this.games.length) {
				this.activeIndex = 0;
			}
		}

		/**
		 * @return void
		 */
		protected previous() {
			this.activeIndex = this.activeIndex - 1;

			if (this.activeIndex < 0) {
				this.activeIndex = this.games.length - 1;
			}
		}

		/**
		 * @return void
		 */
		protected start() {
			ChalkySticks.Utility.Interval.add(
				() => {
					this.next();
				},
				this.intervalDuration,
				'feed-tv-playing-now',
			);

			this.automatic = true;
		}

		/**
		 * @return void
		 */
		protected stop() {
			ChalkySticks.Utility.Interval.remove('feed-tv-playing-now');
			this.automatic = false;
		}

		// region: Event Handlers
		// ---------------------------------------------------------------------------

		/**
		 * @param ChalkySticks.Core.IDispatcherEvent e
		 * @return Promise<void>
		 */
		@bind
		protected async Handle_OnDrag(e: ChalkySticks.Core.IDispatcherEvent<any>): Promise<void> {
			this.hasInteracted = true;
			this.dragRatio = this.pointer.dx / window.innerWidth;

			if (this.automatic) {
				this.stop();
			}
		}

		/**
		 * @param ChalkySticks.Core.IDispatcherEvent e
		 * @return Promise<void>
		 */
		@bind
		protected async Handle_OnDragRelease(e: ChalkySticks.Core.IDispatcherEvent<any>): Promise<void> {
			await gsap.to(this, {
				dragRatio: Math.round(this.dragRatio),
				duration: 0.5,
			});

			if (this.dragRatio > 0.5) {
				this.previous();
			} else if (this.dragRatio < -0.5) {
				this.next();
			} else {
				// Do nothing
			}

			// Reset drag ratio
			this.dragRatio = 0;
		}

		/**
		 * @param PointerEvent e
		 * @return Promise<void>
		 */
		protected async Handle_OnClickWatchNow(e: PointerEvent, index: number): Promise<void> {
			this.$router.push({
				name: 'PageTVSpecific',
				params: {
					id: this.games[index],
				},
			});
		}

		/**
		 * @return Promise<void>
		 */
		@bind
		protected async Handle_OnTap(): Promise<void> {
			this.hasInteracted = true;
		}

		/**
		 * @return Promise<void>
		 */
		protected async Handle_OnVideoPreviewStarting(): Promise<void> {
			await ChalkySticks.Utility.sleep(1000 * 2);

			this.showVideoPreview = true;
		}

		// endregion: Event Handlers
	}
</script>

<style lang="scss">
	.view-feed-tvplayingnow {
		height: 350px;

		> header {
			display: none;
		}

		> .body {
			display: grid;

			.tv-nowplaying {
				grid-area: 1 / 1;
			}

			.tv-scheduleitem {
				height: 100%;
				z-index: 1;

				.title {
					--lh: 1.1em;
					--max-lines: 3;
					max-height: calc(var(--lh) * var(--max-lines));
					overflow: hidden;
					text-overflow: ellipsis;
				}
			}

			.gallery::before,
			.gallery::after {
				display: none;
			}
		}

		> footer {
			bottom: 2rem;
			position: absolute;
			right: 2rem;
			z-index: 10;

			.view-ui-state-dots {
			}
		}

		.button-primary {
			margin-bottom: 0.5rem;
			min-width: 100px;
			padding-left: 0.75em;
			padding-right: 0.75em;
			width: auto;
		}

		.tv-nowplaying .tv-videotheater {
			height: 100%;
			left: 0;
			opacity: 0;
			pointer-events: none;
			position: absolute;
			top: 0;
			transition: opacity 1s var(--ease-in-out-quad);
			width: 100%;
			z-index: 10;
		}

		.tv-nowplaying {
			transform: translateX(calc(-100% + var(--drag-offset) * 100%));

			.content > * {
				transform: translateX(calc(20% - var(--drag-offset) * 20%));
			}
		}

		.tv-nowplaying.state-active {
			transform: translateX(calc(var(--drag-offset) * 100%));

			.content > * {
				transform: translateX(calc(var(--drag-offset) * 20%));
			}
		}

		.tv-nowplaying.state-active ~ .tv-nowplaying {
			transform: translateX(calc(100% + var(--drag-offset) * 100%));

			.content > * {
				transform: translateX(calc(20% + var(--drag-offset) * 20%));
			}
		}

		.tv-nowplaying:first-child.state-active ~ .tv-nowplaying:last-of-type {
			transform: translateX(calc(-100% + var(--drag-offset) * 100%));

			.content > * {
				transform: translateX(calc(20% - var(--drag-offset) * 20%));
			}
		}

		.tv-nowplaying:first-child:not(.state-active) {
			transform: translateX(calc(100% + var(--drag-offset) * 100%));

			.content > * {
				transform: translateX(calc(20% + var(--drag-offset) * 20%));
			}
		}

		.tv-nowplaying:first-child:has(+ .state-active) {
			transform: translateX(calc(-100% + var(--drag-offset) * 100%));

			.content > * {
				transform: translateX(calc(20% - var(--drag-offset) * 20%));
			}
		}

		.tv-nowplaying.state-active + .tv-nowplaying {
			z-index: 5;
		}

		.tv-nowplaying,
		.tv-scheduleitem {
			height: 100%;
			position: relative;

			.content {
				gap: 0.875rem;
				justify-content: flex-end;
			}

			.title {
				font-size: 1.75rem;
				margin-bottom: 0.5rem;
			}

			.subtitle {
				display: none;
			}

			.glass-panel {
				background: linear-gradient(
					180deg,
					rgba(0, 0, 0, 0) 0%,
					rgba(0, 0, 0, 0.7) 70%,
					var(--chalky-blue-3) 95%
				);
			}
		}

		.tv-scheduleitem .content::after {
			background: linear-gradient(
				50deg,
				var(--chalky-red-3) 0%,
				var(--chalky-red-2) 25%,
				rgba(0, 0, 0, 0.5) 70%,
				var(--chalky-blue-3) 95%
			);
			content: ' ';
			height: 100%;
			left: 0;
			mix-blend-mode: normal;
			opacity: 0.25;
			transition: opacity 2s ease-out;
			pointer-events: none;
			position: absolute;
			top: 0;
			width: 100%;
			z-index: -1;
		}
	}

	// Media Queries
	// ---------------------------------------------------------------------

	@include media-query('mobile-landscape') {
		.view-feed-tvplayingnow {
			height: 100dvh;
			max-height: initial;

			.gallery img {
				aspect-ratio: auto;
			}
		}
	}

	// State
	// ---------------------------------------------------------------------

	.view-feed-tvplayingnow.state-videopreview:not(.state-interacted) .tv-videotheater {
		opacity: 1;
	}

	.view-feed-tvplayingnow .tv-nowplaying {
		// opacity: 0;
		pointer-events: none;
		transition: opacity 0.5s ease-in-out;

		&.state-active {
			opacity: 1;
			pointer-events: all;
		}
	}

	.view-feed-tvplayingnow .tv-nowplaying.state-active .content::after {
		opacity: 0.75;
	}
</style>
